import type { FC } from 'react';

import { BugHandleV4Icon } from '../../icons/BugHandleV4Icon';
import { BusinessV4Icon } from '../../icons/BusinessV4Icon';
import { CardV4Icon } from '../../icons/CardV4Icon';
import { ExtensionPuzzleV4Icon } from '../../icons/ExtensionPuzzleV4Icon';
import { GameControllerV4Icon } from '../../icons/GameControllerV4Icon';
import { StatsChartV4Icon } from '../../icons/StatsChartV4Icon';
import { TrendingUpV4Icon } from '../../icons/TrendingUpV4Icon';
import {
  IntegrationsHeroBoltIcon,
  IntegrationsHeroGlobeIcon,
  IntegrationsHeroShieldIcon,
} from '../IntegrationsHero/IntegrationsHeroIcons';
import {
  MethodSpecsGatewayCardIcon,
  MethodSpecsH2HIcon,
  MethodSpecsP2PIcon,
  MethodSpecsWalletIcon,
} from '../IntegrationsMethodSpecsBlock/MethodSpecsIcons';

export type SolutionFeatureItem = {
  id: string;
  title: string;
  description: string;
  Icon: FC;
};

export type SolutionPageDefinition = {
  slug: string;
  metaTitleSuffix: string;
  docDescription: string;
  solutionEyebrowLabel: string;
  headline: string;
  subheadline: string;
  featuresSectionTitle: string;
  features: SolutionFeatureItem[];
  ctaHeadline: string;
  ctaVisualCaption: string;
  card: {
    title: string;
    description: string;
    Icon: FC;
  };
};

export const solutionPages: SolutionPageDefinition[] = [
  {
    slug: 'e-commerce',
    metaTitleSuffix: 'E-commerce',
    docDescription:
      'Turn browsers into buyers with adaptive checkout, instant transaction processing, and broad payment-method support.',
    solutionEyebrowLabel: 'SOLUTION / E-COMMERCE',
    headline: 'Drive Sales with Seamless Checkout',
    subheadline:
      'Turn browsers into buyers with a payment form that adapts to any device. We maximize conversion rates through instant transaction processing and support for all popular payment methods.',
    featuresSectionTitle: 'Key Features',
    ctaHeadline: 'Ready to upgrade your checkout?',
    ctaVisualCaption: 'Payment flows built for conversions',
    card: {
      title: 'E-commerce',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: BusinessV4Icon,
    },
    features: [
      {
        id: 'one-click',
        title: 'One-Click Payments',
        description: 'Secure card saving for effortless repeat purchases.',
        Icon: MethodSpecsGatewayCardIcon,
      },
      {
        id: 'routing',
        title: 'Smart Routing',
        description: 'Minimize declines with intelligent transaction pathing.',
        Icon: MethodSpecsH2HIcon,
      },
      {
        id: 'refunds',
        title: 'Easy Refunds',
        description: 'Manage full or partial returns directly via the dashboard.',
        Icon: MethodSpecsWalletIcon,
      },
    ],
  },
  {
    slug: 'marketplaces',
    metaTitleSuffix: 'Marketplaces',
    docDescription:
      'Automate payouts to many vendors: split a single customer payment, hold commission, and route funds with ease.',
    solutionEyebrowLabel: 'SOLUTION / MARKETPLACES',
    headline: 'Complex Settlements Made Simple',
    subheadline:
      'Automate payouts to hundreds of vendors. Our solution allows you to accept a single payment from a customer and instantly split it, holding your commission and routing the rest.',
    featuresSectionTitle: 'Security & Escrow',
    ctaHeadline: 'Ready to simplify marketplace payouts?',
    ctaVisualCaption: 'Splits, escrow, and vendor automation',
    card: {
      title: 'Marketplaces',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: BugHandleV4Icon,
    },
    features: [
      {
        id: 'escrow',
        title: 'Secure Deals',
        description: 'Hold funds until the customer confirms delivery.',
        Icon: IntegrationsHeroShieldIcon,
      },
      {
        id: 'onboarding',
        title: 'Automated Onboarding',
        description: 'Streamlined KYC for your sub-merchants.',
        Icon: MethodSpecsP2PIcon,
      },
      {
        id: 'reporting',
        title: 'Unified Reporting',
        description: 'Transparent data for every vendor in one place.',
        Icon: MethodSpecsH2HIcon,
      },
    ],
  },
  {
    slug: 'gaming-and-digital-goods',
    metaTitleSuffix: 'Gaming & Digital Goods',
    docDescription:
      'Frictionless microtransactions and in-game purchases with robust fraud protection and global reach.',
    solutionEyebrowLabel: 'SOLUTION / GAMING & DIGITAL GOODS',
    headline: 'Payments at the Speed of Play',
    subheadline:
      'Microtransactions and in-game currency purchases should be invisible. We reduce friction so players stay focused on the game, backed by top-tier fraud protection.',
    featuresSectionTitle: 'Tech Stack',
    ctaHeadline: 'Ready to monetize without friction?',
    ctaVisualCaption: 'Built for gamers worldwide',
    card: {
      title: 'Gaming & Digital goods',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: GameControllerV4Icon,
    },
    features: [
      {
        id: 'iap',
        title: 'In-App Purchases',
        description: 'Seamless integration for mobile and desktop gaming.',
        Icon: MethodSpecsGatewayCardIcon,
      },
      {
        id: 'fraud',
        title: 'Advanced Anti-Fraud',
        description: 'Protect your revenue from chargeback abuse.',
        Icon: IntegrationsHeroShieldIcon,
      },
      {
        id: 'reach',
        title: 'Global Reach',
        description: 'Accept payments from gamers in any currency, anywhere.',
        Icon: IntegrationsHeroGlobeIcon,
      },
    ],
  },
  {
    slug: 'subscription-services',
    metaTitleSuffix: 'Subscription Services',
    docDescription: 'Automate recurring billing, failed-payment recovery, and self-service subscription management.',
    solutionEyebrowLabel: 'SOLUTION / SUBSCRIPTION SERVICES',
    headline: 'Predictable Growth with Zero Effort',
    subheadline:
      'Automate your subscription billing. Our system handles recurring charges and automatically notifies customers about expiring cards.',
    featuresSectionTitle: 'Retention Tools',
    ctaHeadline: 'Ready to grow recurring revenue?',
    ctaVisualCaption: 'Billing that keeps subscribers on board',
    card: {
      title: 'Subscription services',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: ExtensionPuzzleV4Icon,
    },
    features: [
      {
        id: 'retries',
        title: 'Smart Retries',
        description: 'Intelligent logic to recover failed payments and reduce churn.',
        Icon: IntegrationsHeroBoltIcon,
      },
      {
        id: 'plans',
        title: 'Flexible Plans',
        description: 'Easily manage trials, tiers, and billing cycles.',
        Icon: MethodSpecsH2HIcon,
      },
      {
        id: 'portal',
        title: 'Subscription Portal',
        description: 'Give users control over their payment methods.',
        Icon: MethodSpecsP2PIcon,
      },
    ],
  },
  {
    slug: 'fintech',
    metaTitleSuffix: 'Fintech',
    docDescription:
      'API-first infrastructure with white-label options, transfers, PCI alignment, and high availability.',
    solutionEyebrowLabel: 'SOLUTION / FINTECH',
    headline: 'Built for Financial Innovators',
    subheadline:
      'Build your own financial products on top of our robust API. We provide White Label solutions and tools for instant fund transfers between accounts and cards.',
    featuresSectionTitle: 'Compliance & Reliability',
    ctaHeadline: 'Ready to ship on scalable payments infrastructure?',
    ctaVisualCaption: 'Compliance-ready building blocks',
    card: {
      title: 'Fintech',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: CardV4Icon,
    },
    features: [
      {
        id: 'security',
        title: 'Total Security',
        description: 'Full PCI DSS compliance and regional regulatory alignment.',
        Icon: IntegrationsHeroShieldIcon,
      },
      {
        id: 'uptime',
        title: 'High Availability',
        description: '99.99% uptime to keep your services running 24/7.',
        Icon: IntegrationsHeroBoltIcon,
      },
      {
        id: 'logic',
        title: 'Customizable Logic',
        description: 'Set your own limits and risk-monitoring rules.',
        Icon: MethodSpecsGatewayCardIcon,
      },
    ],
  },
  {
    slug: 'fx-trading',
    metaTitleSuffix: 'FX / Trading',
    docDescription:
      'Fast deposits and withdrawals for trading platforms, with robust verification and multi-currency support.',
    solutionEyebrowLabel: 'SOLUTION / FX / TRADING',
    headline: 'Instant Funding for Fast Movers',
    subheadline:
      'In trading, every second counts. Provide your clients with instant deposits and rapid withdrawals so they can react to market shifts without delay.',
    featuresSectionTitle: 'Risk Management',
    ctaHeadline: 'Ready to fund traders in real time?',
    ctaVisualCaption: 'Move money at market speed',
    card: {
      title: 'FX / trading',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: TrendingUpV4Icon,
    },
    features: [
      {
        id: 'verification',
        title: 'Multi-Level Verification',
        description: 'Secure transaction validation for high-volume trades.',
        Icon: IntegrationsHeroShieldIcon,
      },
      {
        id: 'risk',
        title: 'High-Risk Expertise',
        description: 'Specialized tools for managing high-risk environments.',
        Icon: MethodSpecsH2HIcon,
      },
      {
        id: 'multicurrency',
        title: 'Multi-Currency Support',
        description: 'Automatic conversion and global settlement.',
        Icon: IntegrationsHeroGlobeIcon,
      },
    ],
  },
  {
    slug: 'regulated-industries',
    metaTitleSuffix: 'Regulated Industries',
    docDescription:
      'Transparent flows, reporting tailored to licensing, with dedicated support and pricing for highly regulated sectors.',
    solutionEyebrowLabel: 'SOLUTION / REGULATED INDUSTRIES',
    headline: 'Reliability Within the Legal Framework',
    subheadline:
      'We understand the nuances of licensed businesses. Our solution is tailored to meet strict reporting requirements and ensure full transparency of every payment flow.',
    featuresSectionTitle: 'Tailored Approach',
    ctaHeadline: 'Ready for compliant payment operations?',
    ctaVisualCaption: 'Partners for regulated workflows',
    card: {
      title: 'Regulated industries',
      description: 'High conversion checkout, fast payouts, and granular controls',
      Icon: StatsChartV4Icon,
    },
    features: [
      {
        id: 'support',
        title: 'Dedicated Support',
        description: 'Personal account managers for complex use cases.',
        Icon: MethodSpecsP2PIcon,
      },
      {
        id: 'analytics',
        title: 'Deep Analytics',
        description: 'Comprehensive data exports for auditing purposes.',
        Icon: MethodSpecsH2HIcon,
      },
      {
        id: 'rates',
        title: 'Custom Rates',
        description: 'Flexible pricing models that account for industry-specific volumes.',
        Icon: MethodSpecsWalletIcon,
      },
    ],
  },
];

export const solutionPageSlugs = solutionPages.map((p) => p.slug);

export function getSolutionPageBySlug(slug: string): SolutionPageDefinition | undefined {
  return solutionPages.find((p) => p.slug === slug);
}
